import {
  faChevronDown,
  faChevronUp,
  faLock,
  faPlus,
  faTrashCan,
  faUnlock
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import get from 'lodash/get';
import { Fragment, useContext, useMemo, useState } from 'react';
import {
  ArrayPath,
  Control,
  FieldArrayWithId,
  FieldErrors,
  FieldPath,
  FieldValues,
  useFieldArray,
  UseFormGetValues
} from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import { hasPermission, PEOPLE_INSURANCE_POLICY_ADMINISTRATOR } from '@/utils/userPermission';

import ArrayFieldController from './ArrayFieldController';
import type { ArrayFieldEditorProps, ArrayFieldValue, FieldOfArrayField } from './types';

type ArrayField<T extends FieldValues> = FieldArrayWithId<T, ArrayPath<T>, 'arrayId'> & {
  _delete: boolean;
};

function ArrayFieldEditor<T extends FieldValues>({
  control,
  fieldKey,
  fields: shapeFields,
  newObject,
  errors,
  getFormValue,
  deleteConfirmMessage
}: ArrayFieldEditorProps<T>) {
  let fieldLabel = 0;
  const { append, fields, remove, update } = useFieldArray({
    control,
    name: fieldKey,
    keyName: 'arrayId'
  });

  const visibleFields = useMemo(
    () => shapeFields.filter(({ hidden }) => !hidden) || [],
    [shapeFields]
  );
  const hiddenFields = useMemo(
    () => shapeFields.filter(({ hidden }) => hidden) || [],
    [shapeFields]
  );

  const handleDelete = (idx: number) => {
    const isLastRemaining =
      (fields as ArrayField<T>[]).filter((field, i) => i !== idx && !field._delete).length === 0;
    if (deleteConfirmMessage && isLastRemaining && !confirm(deleteConfirmMessage)) return;

    const fieldValue = getFormValue(`${fieldKey}.${idx}` as FieldPath<T>);
    if (fieldValue.id) update(idx, { ...fieldValue, _delete: true } as ArrayField<T>);
    else remove(idx);
  };

  const handleAppend = () => {
    if (typeof newObject === 'function') {
      const buildNewObject = newObject as (
        rows: ArrayField<T>[]
      ) => ArrayFieldValue<T, ArrayPath<T>>;
      append(buildNewObject(fields as ArrayField<T>[]));
    } else {
      append(newObject);
    }
  };

  return (
    <>
      <div>
        {(fields as ArrayField<T>[]).map((field, idx) => {
          ++fieldLabel;

          return (
            <ArrayFields
              key={`${fieldKey}-${idx}`}
              control={control}
              errors={errors}
              field={field}
              fieldKey={fieldKey}
              fieldLabel={fieldLabel}
              getFormValue={getFormValue}
              idx={idx}
              handleDelete={handleDelete}
              hiddenFields={hiddenFields}
              visibleFields={visibleFields}
            />
          );
        })}
      </div>
      <div className="flex justify-center my-4">
        <Button ariaLabel="Add new record" size="xs" onClick={handleAppend}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add new
        </Button>
      </div>
    </>
  );
}

const ArrayFields = <T extends FieldValues>({
  control,
  errors,
  field,
  fieldKey,
  fieldLabel,
  getFormValue,
  idx,
  handleDelete,
  hiddenFields,
  visibleFields
}: {
  control: Control<T>;
  errors: FieldErrors<T>;
  field: ArrayField<T>;
  fieldKey: ArrayPath<T>;
  fieldLabel: number;
  getFormValue: UseFormGetValues<T>;
  idx: number;
  handleDelete: (idx: number) => void;
  hiddenFields: FieldOfArrayField<T>[];
  visibleFields: FieldOfArrayField<T>[];
}) => {
  const { permissions } = useContext(AppContext);
  const canEditImmutableInsurancePolicies = hasPermission({
    permissions,
    requiredPermission: PEOPLE_INSURANCE_POLICY_ADMINISTRATOR
  });
  const [isMutable, setIsMutable] = useState<boolean>(
    getFormValue(`${fieldKey}.${idx}.is_mutable` as FieldPath<T>) ?? true
  );
  const isInsurancePolicy = fieldKey === 'insurance_policies';
  const isMutableOverrides = isInsurancePolicy && canEditImmutableInsurancePolicies;
  const cannotBeEditedMessage = isInsurancePolicy
    ? 'This insurance policy is verified and cannot be edited.'
    : 'This record is immutable and cannot be edited.';

  const hasHiddenFields = useMemo(() => !!hiddenFields.length, [hiddenFields]);

  return !field._delete ? (
    <div
      key={`${fieldKey}-${idx}`}
      className="py-2 border-b-2 relative first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex justify-between mb-1">
        <div>
          <label className="inline-block text-base font-medium text-gray-700">#{fieldLabel}</label>
          {!isMutable && <FontAwesomeIcon icon={faLock} className="ml-1 text-red-600" />}
        </div>

        {!isMutable && isMutableOverrides && (
          <Button
            ariaLabel="Unlock record"
            color={Color.WHITE}
            onClick={() =>
              confirm('Are you sure you want to unlock and make edits to this record?')
                ? setIsMutable(true)
                : null
            }
            size="xs"
            type="button">
            <FontAwesomeIcon icon={faUnlock} className="mr-1" />
            Unlock
          </Button>
        )}
      </div>

      {!isMutable && <Alert level="warn" message={cannotBeEditedMessage} />}
      <div>
        {visibleFields.map((shapeField) => {
          const fieldKey = shapeField.key(idx);
          return (
            <Fragment key={fieldKey}>
              {get(errors, fieldKey)?.message && (
                <Alert level="error" message={String(get(errors, fieldKey)?.message)} />
              )}
              <ArrayFieldController
                control={control}
                index={idx}
                isMutable={isMutable}
                field={shapeField}
              />
            </Fragment>
          );
        })}

        {hasHiddenFields && (
          <HiddenFields
            control={control}
            errors={errors}
            idx={idx}
            isMutable={isMutable}
            hiddenFields={hiddenFields}
          />
        )}

        {isMutable && (
          <Button
            ariaLabel="Delete record"
            className="absolute top-2 right-2"
            color={Color.RED}
            onClick={() => handleDelete(idx)}
            size="xs">
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        )}
      </div>
    </div>
  ) : null;
};

const HiddenFields = <T extends FieldValues>({
  control,
  errors,
  idx,
  isMutable,
  hiddenFields
}: {
  control: Control<T>;
  errors: FieldErrors<T>;
  idx: number;
  isMutable: boolean;
  hiddenFields: FieldOfArrayField<T>[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <div className="flex justify-center mt-3">
        <Button
          ariaLabel={`Show ${isExpanded ? 'less' : 'more'}`}
          disabled={!isMutable}
          size="xs"
          onClick={() => setIsExpanded(!isExpanded)}>
          Show {isExpanded ? 'less' : 'more'}
          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="ml-2" />
        </Button>
      </div>

      {isExpanded &&
        hiddenFields.map((shapeField) => {
          const fieldKey = shapeField.key(idx);
          return (
            <Fragment key={fieldKey}>
              {get(errors, fieldKey)?.message && (
                <Alert level="error" message={String(get(errors, fieldKey)?.message)} />
              )}
              <ArrayFieldController
                control={control}
                index={idx}
                isMutable={isMutable}
                field={shapeField}
              />
            </Fragment>
          );
        })}
    </>
  );
};

export default ArrayFieldEditor;
